import React, { useState } from 'react';
import { RemittanceRecord } from '../services/api';
import { DollarSign, Calendar, Hash, ChevronDown, ChevronRight, Users } from 'lucide-react';

interface GroupedRemittanceTableProps {
  records: RemittanceRecord[];
  loading?: boolean;
}

interface GroupedRecord {
  customer_name: string;
  payment_date: string;
  records: RemittanceRecord[];
  total_amount: number;
  count: number;
  banks: string[];
  pages: string[];
  notes: string[];
}

const GroupedRemittanceTable: React.FC<GroupedRemittanceTableProps> = ({ records, loading = false }) => {
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // 當records變化時，重置展開狀態
  React.useEffect(() => {
    setExpandedGroups(new Set());
  }, [records]);

  // 將記錄按客戶名稱和匯款日期分組 - 在早期返回之前處理
  const groupedRecords: GroupedRecord[] = React.useMemo(() => {
    if (records.length === 0) return [];
    
    const groups = records.reduce((acc, record) => {
      const groupKey = `${record.customer_name}_${record.payment_date}`;
      if (!acc[groupKey]) {
        acc[groupKey] = [];
      }
      acc[groupKey].push(record);
      return acc;
    }, {} as Record<string, RemittanceRecord[]>);

    return Object.entries(groups)
      .map(([groupKey, groupRecords]) => {
        const sortedRecords = groupRecords.sort((a, b) => new Date(b.payment_date).getTime() - new Date(a.payment_date).getTime());
        
        // 收集該組的所有銀行、分頁和備註
        const banks = Array.from(new Set(sortedRecords.filter(r => r.bank_name).map(r => r.bank_name!)));
        const pages = Array.from(new Set(sortedRecords.filter(r => r.page).map(r => r.page!)));
        const notes = Array.from(new Set(sortedRecords.filter(r => r.note && r.note.trim()).map(r => r.note!)));
        
        return {
          customer_name: sortedRecords[0].customer_name,
          payment_date: sortedRecords[0].payment_date,
          records: sortedRecords,
          total_amount: groupRecords.reduce((sum, record) => sum + record.amount, 0),
          count: groupRecords.length,
          banks,
          pages,
          notes,
        };
      })
      .sort((a, b) => {
        // 先按客戶名稱排序，再按總金額降序排列
        if (a.customer_name !== b.customer_name) {
          return a.customer_name.localeCompare(b.customer_name);
        }
        return b.total_amount - a.total_amount;
      });
  }, [records]);

  const toggleGroup = (customerName: string, paymentDate: string) => {
    const groupKey = `${customerName}_${paymentDate}`;
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupKey)) {
      newExpanded.delete(groupKey);
    } else {
      newExpanded.add(groupKey);
    }
    setExpandedGroups(newExpanded);
  };



  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-4 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (records.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6 sm:p-8 text-center">
        <div className="text-gray-500">
          <DollarSign className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-medium mb-2">暫無記錄</h3>
          <p className="text-sm">請嘗試調整搜尋條件或檢查資料是否存在</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200">
        <h3 className="text-base sm:text-lg font-semibold text-gray-800 flex items-center">
          <Users className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600 mr-2" />
          客戶分組匯款記錄 ({groupedRecords.length} 組記錄，{records.length} 筆明細)
        </h3>
      </div>
      
      {/* 手機版 - 卡片式佈局 */}
      <div className="block sm:hidden">
        <div className="divide-y divide-gray-200">
          {groupedRecords.map((group) => (
            <div key={group.customer_name} className="bg-white">
              {/* 客戶摘要行 */}
                             <div 
                 className="p-4 hover:bg-gray-50 transition-colors cursor-pointer"
                 onClick={() => toggleGroup(group.customer_name, group.payment_date)}
               >
                 <div className="flex items-center justify-between">
                   <div className="flex items-center space-x-3">
                     {expandedGroups.has(`${group.customer_name}_${group.payment_date}`) ? (
                       <ChevronDown className="h-4 w-4 text-gray-400" />
                     ) : (
                       <ChevronRight className="h-4 w-4 text-gray-400" />
                     )}
                    <div>
                      <div className="font-medium text-gray-900">{group.customer_name}</div>
                      <div className="text-sm text-gray-500">{group.count} 筆記錄</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-semibold text-green-600">
                      {group.total_amount.toLocaleString('zh-TW', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}
                    </div>
                                         <div className="text-xs text-gray-500">
                       {group.payment_date}
                     </div>
                  </div>
                </div>
              </div>

                             {/* 展開的詳細記錄 */}
               {expandedGroups.has(`${group.customer_name}_${group.payment_date}`) && (
                <div className="bg-gray-50 border-t border-gray-200">
                  {group.records.map((record) => (
                    <div key={record.id} className="p-4 border-b border-gray-100 last:border-b-0">
                      <div className="space-y-2">
                        <div className="flex justify-between items-start">
                          <div className="flex items-center text-sm text-gray-500">
                            <Hash className="h-3 w-3 mr-1" />
                            {record.id}
                          </div>
                          <div className="flex items-center">
                            <Calendar className="h-4 w-4 text-purple-600 mr-1" />
                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                              {record.payment_date}
                            </span>
                          </div>
                        </div>
                        
                                                 <div className="flex justify-between items-center">
                           <div className="flex items-center">
                             <DollarSign className="h-4 w-4 text-green-600 mr-1" />
                             <span className="text-sm font-semibold text-green-600">
                               {record.amount.toLocaleString('zh-TW', {
                                 minimumFractionDigits: 2,
                                 maximumFractionDigits: 2
                               })}
                             </span>
                           </div>
                         </div>
                         
                         {/* 銀行、分頁、備註資訊 */}
                         <div className="space-y-1 text-xs text-gray-600">
                           {record.bank_name && (
                             <div>銀行：<span className="font-medium text-gray-800">{record.bank_name}</span></div>
                           )}
                           {record.page && (
                             <div>分頁：<span className="font-medium text-indigo-600">{record.page}</span></div>
                           )}
                           {record.note && (
                             <div>備註：<span className="font-medium text-orange-600">{record.note}</span></div>
                           )}
                         </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* 桌面版 - 表格佈局 */}
      <div className="hidden sm:block overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                客戶名稱
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                記錄數量
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <div className="flex items-center">
                  <DollarSign className="h-4 w-4 mr-1" />
                  總匯款單位
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <div className="flex items-center">
                  <Calendar className="h-4 w-4 mr-1" />
                  匯款日期
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                操作
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {groupedRecords.map((group) => (
              <React.Fragment key={`${group.customer_name}_${group.payment_date}`}>
                {/* 客戶摘要行 */}
                <tr className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="font-medium text-gray-900">{group.customer_name}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                      {group.count} 筆
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm font-semibold text-green-600">
                      {group.total_amount.toLocaleString('zh-TW', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {group.payment_date}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => toggleGroup(group.customer_name, group.payment_date)}
                      className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-blue-700 bg-blue-100 hover:bg-blue-200 transition-colors"
                    >
                      {expandedGroups.has(`${group.customer_name}_${group.payment_date}`) ? (
                        <>
                          <ChevronDown className="h-3 w-3 mr-1" />
                          收起
                        </>
                      ) : (
                        <>
                          <ChevronRight className="h-3 w-3 mr-1" />
                          展開
                        </>
                      )}
                    </button>
                  </td>
                </tr>

                {/* 展開的詳細記錄 */}
                {expandedGroups.has(`${group.customer_name}_${group.payment_date}`) && group.records.map((record) => (
                  <tr key={record.id} className="bg-gray-50">
                    <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500 pl-12">
                      #{record.id}
                    </td>
                    <td className="px-6 py-3 whitespace-nowrap">
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                        {record.payment_date}
                      </span>
                    </td>
                    <td className="px-6 py-3 whitespace-nowrap">
                      <span className="text-sm font-medium text-green-600">
                        {record.amount.toLocaleString('zh-TW', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}
                      </span>
                    </td>
                                         <td className="px-6 py-3 text-sm text-gray-500">
                       <div className="space-y-1">
                         {record.bank_name && (
                           <div className="text-xs text-gray-600">
                             銀行：<span className="font-medium text-gray-800">{record.bank_name}</span>
                           </div>
                         )}
                         {record.page && (
                           <div className="text-xs text-gray-600">
                             分頁：<span className="font-medium text-indigo-600">{record.page}</span>
                           </div>
                         )}
                         {record.note && (
                           <div className="text-xs text-gray-600">
                             備註：<span className="font-medium text-orange-600">{record.note}</span>
                           </div>
                         )}
                       </div>
                     </td>
                     <td className="px-6 py-3 text-sm text-gray-500">
                       {/* 操作欄位已移到前面 */}
                     </td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default GroupedRemittanceTable; 